import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {useBusiness, useCapabilities, type Business} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import casimiroGuardian from '@/assets/casimiro/casimiro-guardian.webp'

export function sgsstPageGuard(loading: boolean, hasOwnerAccess: boolean, state: unknown): React.ReactElement | null {
  if (loading) return <Skeleton className="h-60 w-full" />
  if (!hasOwnerAccess) return <NoAccessState />
  if (!state) return <Skeleton className="h-60 w-full" />
  return null
}

/** `business` is non-null exactly when `guard` is null. */
export function useOperatorPage(businessId: string | undefined): {
  business: Business | undefined
  canEdit: boolean
  guard: React.ReactElement | null
} {
  const {data: business, isLoading} = useBusiness(businessId)
  const canOperate = useCapabilities(businessId).sgsst_operar
  const godReadOnly = useGodModeReadOnly()
  const canEdit = canOperate && !godReadOnly
  return {business, canEdit, guard: sgsstPageGuard(isLoading, canOperate, business)}
}

/** Same state for «not a member» and «business gone»: it leaks nothing about the target. */
export function NoAccessState() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  return (
    <div className="flex items-start gap-4 sm:gap-5">
      <img
        src={casimiroGuardian}
        alt={t('business.noAccessAlt')}
        loading="lazy"
        decoding="async"
        className="h-28 w-auto shrink-0 sm:h-36"
      />
      <div className="space-y-3">
        <h1 className="font-heading text-xl text-foreground">{t('business.noAccessTitle')}</h1>
        <p className="text-sm text-muted-foreground">{t('business.noAccessBody')}</p>
        <Button size="sm" variant="outline" onClick={() => navigate('/')}>
          {t('business.backToBusinesses')}
        </Button>
      </div>
    </div>
  )
}
